import React from "react";
import { useTenant } from "../TenantContext"; // Ensure this path is correct

interface IconProps {
    isActive: boolean;
    isFilled?: boolean; // Filled state has a white icon on a colored background
    color?: string; // Dynamic color prop (overrides tenant config)
    inactiveColor?: string; // Dynamic inactive color prop
}

/**
 * Custom Hook to handle the color logic, including accessing the tenant configuration.
 * @param isActive - Whether the icon is currently active.
 * @param isFilled - Whether the icon should be filled (white icon on colored background).
 * @param propColor - Optional color passed directly as a prop.
 * @param propInactiveColor - Optional inactive color passed directly as a prop.
 * @returns { fillColor, strokeColor } - The calculated fill and stroke colors.
 */
const useIconColors = ({ isActive, isFilled, color: propColor, inactiveColor: propInactiveColor }: IconProps) => {
    const { tenantConfig } = useTenant();

    // Prefer tenant primary color, fall back to a sensible default
    const TENANT_PRIMARY = tenantConfig?.authority?.authorityBranding?.primaryColor || '#095CC9';

    // Determine final fill color:
    // Priority:
    // 1. If a propColor/propInactiveColor is provided, use it (this allows callers to force a color
    //    even for 'filled' states such as the disabled gray case).
    // 2. If icon is rendered as filled circle and no explicit prop color was provided, use white.
    // 3. Otherwise fall back to tenant primary color.
    const getFillColor = () => {
        if (isActive && propColor) return propColor;
        if (!isActive && propInactiveColor) return propInactiveColor;
        if (isFilled) return 'white';
        return TENANT_PRIMARY;
    };

    const getStrokeColor = () => {
        if (isActive && propColor) return propColor;
        if (!isActive && propInactiveColor) return propInactiveColor;
        if (isFilled) return 'white';
        return TENANT_PRIMARY;
    };

    return {
        fillColor: getFillColor(),
        strokeColor: getStrokeColor(),
    };
};

// --- Icon Components ---

export const StudentIcon = (props: IconProps) => {
    // Use the custom hook to get the colors
    const { fillColor } = useIconColors(props);
    return (
        <span
            className="material-symbols-outlined"
            style={{ color: fillColor, fontSize: '24px' }}
            aria-hidden="true"
        >
            person_2
        </span>
    );
};

export const AcademicIcon = (props: IconProps) => {
    const { fillColor } = useIconColors(props);
    return (
        <span
            className="material-symbols-outlined"
            style={{ color: fillColor, fontSize: '24px' }}
            aria-hidden="true"
        >
            school
        </span>
    );
};

export const FamilyIcon = (props: IconProps) => {
    const { fillColor } = useIconColors(props);
    return (
        <span
            className="material-symbols-outlined"
            style={{ color: fillColor, fontSize: '24px' }}
            aria-hidden="true"
        >
            family_group
        </span>
    );
};

export const ParentFinancesIcon = (props: IconProps) => {
    const { fillColor } = useIconColors(props);
    return (
        <span
            className="material-symbols-outlined"
            style={{ color: fillColor, fontSize: '24px' }}
            aria-hidden="true"
        >
            account_balance
        </span>
    );
};

export const StudentFinancesIcon = (props: IconProps) => {
    const { fillColor } = useIconColors(props);
    return (
        <span
            className="material-symbols-outlined"
            style={{ color: fillColor, fontSize: '24px' }}
            aria-hidden="true"
        >
            savings
        </span>
    );
};

export const SummaryIcon = (props: IconProps) => {
    const { fillColor } = useIconColors(props);
    return (
        <span
            className="material-symbols-outlined"
            style={{ color: fillColor, fontSize: '24px' }}
            aria-hidden="true"
        >
            summarize
        </span>
    );
};